<template>
  <div>
    <div class="form-group form-row">
      <div class="col">
        <div class="card rule-builder" :class="{ 'disabled': isDisabled }">
          <h5 class="card-header">{{ $t("Webportal.RuleBuilder") }}</h5>
          <!-- HACK: Empty <div> to avoid css style for removing top border of nested list groups -->
          <div></div>
          <div v-if="value" class="list-group list-group-flush border-top-0 overflow-auto">
            <rule-item 
              v-model="value" 
              :options="options" 
              :canDelete="false" 
              :disabled="isDisabled"
            />
          </div>
        </div>
      </div>
      <div class="col">
        <rule-properties 
          v-model="options.selectedItem" 
          :options="options"
          :disabled="isDisabled"
        />
      </div>
    </div>

    <div class="card rule-formula" :class="{ 'disabled': isDisabled }">
      <h5 class="card-header">{{ $t("Webportal.RuleBuilder.Formula") }}</h5>
      <div class="card-body">
        <textarea
          :value="formulaFormatted"
          class="form-control"
          :style="{ fontFamily: 'Courier' }"
          readonly
          rows="4"
          :disabled="isDisabled">
        </textarea>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import EventBus from '@/services/EventBus.js';
import SurveysService from "@/services/SurveysService";
import AdministationService from '@/services/AdministationService';
import RuleItem from './RuleItem';
import RuleProperties from './RuleProperties';
import RuleBuilderMixin from './RuleBuilderMixin';

export default {
  name: 'RuleBuilder',

  mixins: [ RuleBuilderMixin ],
  
  components: {
    RuleItem,
    RuleProperties
  },

  props: {
    value: null,
    surveyId: { type: Number, default: null },
    disabled: { type: Boolean, default: false }
  },
  
  data() {
    return {
      propertiesCancelToken: null,
      clientLocationsCancelToken: null,

      options: {
        waves: [],
        scenarios: [],
        properties: [],
        propertiesFlat: [],
        clientLocations: [],
        isBusy: false,
        selectedItem: null,
        itemAdded: this.itemAdded,
        itemClicked: this.itemClicked,
        itemDeleted: this.itemDeleted,
        itemChanged: this.itemChanged
      }
    }
  },

  computed: {
    isValid() {
      let result = true;

      if (this.value != null)
        this.traverseAllItems(this.value, item => { if (!this.isRuleItemValid(item)) result = false; } );

      return result;
    },

    isDisabled() {
      return this.disabled || this.options?.isBusy;
    },

    formula() {
      if (this.value == null || this.options.isBusy || !this.isValid)
        return null;

      return this.calculateFormula(this.value, false);
    },

    formulaFormatted() {
      if (this.value == null || this.options.isBusy || !this.isValid)
        return null;

      return this.calculateFormula(this.value, true);
    }
  },

  watch: {
    surveyId() {
      this.loadData();
    },

    value() {
      this.options.selectedItem = null;
    },

    formula(newValue) {
      this.$emit('update:formula', newValue);
    }
  },

  mounted() {
    this.loadData();

    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeDestroy() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  methods: {
    localizationChanged(localizationId) {
      this.loadData();
    },

    calculateFormula(item, formatted, level = 0) {
      let ruleValueObject = this.getRuleValueObject(item);

      if (item.type === this.RuleType.Wave) {
        let waveCondition = 'SurveyWaveId ' +
          (item.operation === this.RuleOperation.Equals || item.operation === this.RuleOperation.GreaterOrEqual ? '=' : '<>') +
          ' ' + item.value;

        if (item.operation === this.RuleOperation.GreaterOrEqual)
          waveCondition = `(${waveCondition} OR SurveyWaveSortOrder > ${ruleValueObject.SortOrder})`;

          return waveCondition;
      }
      else if (item.type === this.RuleType.Scenario) {
        return `ScenarioId ${item.operation} ${item.value}`;
      }
      else if (item.type === this.RuleType.Property) {
        let propertyValue = item.value.substring(item.value.indexOf('^') + 1);
        return `Properties["${ruleValueObject.parent.label}"] ${item.operation} "${propertyValue}"`;
      }
      else if (item.type === this.RuleType.Location) {
        return `ClientLocationId ${item.operation} ${item.value}`;
      }
      else if (item.type == null && item.children.length > 0)
      {
        let formatting = formatted ? (level > 0 ? '\r\n' : '') + ''.padEnd(level * 2, ' ') : '';
        let negation = item.operation === this.GroupOperation.NotAnd || item.operation === this.GroupOperation.NotOr ? 'NOT ' : '';
        let operation = item.operation === this.GroupOperation.And || item.operation === this.GroupOperation.NotAnd ? 'AND' : 'OR';

        return formatting +
          negation + '(' +
          item.children
            .map(childItem => this.calculateFormula(childItem, formatted, level + 1))
            .filter(f => f != null)
            .join(' ' + operation + ' ') +
          ')';
      }

      return null;
    },

    traverseAllItems(item, callback) {
      callback(item);

      if (item.children)
        item.children.forEach(childItem => this.traverseAllItems(childItem, callback));
    },

    itemAdded(item) {
      if (item.type !== undefined)
        this.options.selectedItem = item;

      this.$emit('item-added', item);
      this.$emit('input', this.value);
    },

    itemClicked(item) {
      this.options.selectedItem = item;

      this.$emit('item-selected', item);
    },

    itemDeleted(item) {
      let selectedItemFound = false;

      // Check if selected item still exists in data or if it was deleted
      this.traverseAllItems(this.value, item => { if (this.options.selectedItem === item) selectedItemFound = true; } );

      if (!selectedItemFound)
        this.options.selectedItem = null;

      this.$emit('item-deleted', item);
      this.$emit('input', this.value);
    },

    itemChanged(item) {
      this.$emit('item-changed', item);
      this.$emit('input', this.value);
    },

    getClientLocationItem(clientLocation) {
      return {
        id: clientLocation.ClientLocationId,
        label: [
          clientLocation.ClientLocationIdOnClientSite ?? '',
          clientLocation.Name ?? '',
          clientLocation.PostalCode ?? '',
          clientLocation.City ?? ''
        ].join(' ')
      };
    },

    loadData() {
      this.options.properties = [];
      this.options.clientLocations = [];

      if (this.surveyId) {
        this.options.isBusy = true;

        this.propertiesCancelToken?.cancel();
        this.clientLocationsCancelToken?.cancel();

        AdministationService.getSurveySettings(this.surveyId)
          .then((response) => {
            let clientId = response.data[0].Client.ClientId;

            this.propertiesCancelToken = axios.CancelToken.source();
            this.clientLocationsCancelToken = axios.CancelToken.source();

            return Promise.all([
              SurveysService.getSurveyWaves({ surveyIds: [ this.surveyId ] }),
              SurveysService.getSurveyScenarios({ surveyIds: [ this.surveyId ] }),
              SurveysService.getSurveyCustomProperties({ clientIds: [ clientId ] }, true, false, true, this.propertiesCancelToken),
              AdministationService.getClientLocations({ clientIds: [ clientId ] }, {}, this.clientLocationsCancelToken)
            ])
              .then(([responseWaves, responseScenarios, responseProperties, responseLocations]) => {
                this.options.waves = responseWaves.data;
                this.options.scenarios = responseScenarios.data;
                this.options.properties = responseProperties.data;
                this.options.propertiesFlat = _(this.options.properties)
                  .flatMapDeep(item => item.children != null ? item.children.map(childItem => { childItem.parent = item; return childItem; }) : item)
                  .flatMapDeep(item => item.children != null ? item.children.map(childItem => { childItem.parent = item; return childItem; }) : item)
                  .value();
                this.options.clientLocations = responseLocations.data.items.map(item => this.getClientLocationItem(item.ClientLocation)) ?? [];
                this.scenarioItems = response.data;
              })
          })
          .finally(_ => {
            this.options.isBusy = false;
          });
      }
    }
  }
}
</script>

<style lang="scss">
  .rule-builder {
    height: 100%;
    min-height: 350px;
    max-height: 500px;

    &.disabled {
      opacity: 0.65;
    }
  }

  .rule-formula {
    &.disabled {
      opacity: 0.65;
    }
  }
</style>